import axios from 'axios';
import type { InjectionKey } from 'vue';
import type { ServiceResult } from '@core/models/actionResponse';
import { isServiceSuccess } from '@core/models/actionResponse';
import type { TokenRequest, TokenResponse } from './models/authModels';
import { axiosToServiceResult } from './axios';

export const AUTH_KEY: InjectionKey<IAuthenticationHandler> = Symbol();
export interface IAuthenticationHandler {
  getToken(opts?: TokenRequest): Promise<ServiceResult<TokenResponse>>;
  getClaims(): Promise<Record<string, string>>;
}

export class AuthenticationHandler implements IAuthenticationHandler {
  private currentToken: TokenResponse | null;

  constructor() {
    this.currentToken = null;
  }

  async getClaims(): Promise<Record<string, string>> {
    if (!this.currentToken) {
      await this.getToken();
    }

    return this.currentToken ? parseJwt(this.currentToken.token) : {};
  }

  async getToken(opts?: TokenRequest): Promise<ServiceResult<TokenResponse>> {
    const now = new Date();
    if (!opts && this.currentToken && this.currentToken.expiration > now) {
      return { data: this.currentToken };
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!opts && refreshToken) {
      return await this.loginWithRefreshToken(refreshToken);
    }

    if (!opts) {
      return { errors: [`Failed to authenticate`] };
    }

    try {
      return this.processTokenResponse(await this.obtainToken(opts));
    } catch {
      return { errors: [`Failed to authenticate`] };
    }
  }

  async loginWithRefreshToken(token: string) {
    const response = await this.refreshToken(token);
    
    if (!isServiceSuccess(response) && response.errors && response.errors.includes('Invalid token')) {
      this.clear();
      console.log('Session expired. Redirecting to login...');
      window.location.href = `${window.location.origin}/account/login?expired=1`;
      return { errors: ['Session expired. Please log in again.'] };
    }
    return this.processTokenResponse(response);
  }

  private processTokenResponse(response: ServiceResult<TokenResponse>) {
    if (isServiceSuccess(response)) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      this.currentToken = {
        ...response.data,
        expiration: new Date(response.data.expiration),
      };
    }
    return response;
  }

  private async obtainToken(req: TokenRequest): Promise<ServiceResult<TokenResponse>> {
    return await axiosToServiceResult(axios.post<TokenResponse>('/api/auth/login', req));
  }

  private async refreshToken(refreshToken: string): Promise<ServiceResult<TokenResponse>> {
    return await axiosToServiceResult(axios.post<TokenResponse>('/api/auth/refresh', { refreshToken }));
  }

  clear() {
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authToken');
  }
}

export const authenticationHandler = new AuthenticationHandler();

export function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}
